import React, { useState } from "react";
import ContentPasteGo from "@mui/icons-material/ContentPasteGo";
import { FormHelperText } from "@mui/material";
import Button from "@components/Button/Button";
import { MessageBoxContainer, MessageInput } from "@components/Chat/Chat.styles";
import SingleFile from "@components/FileUpload/SingleFile";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import type { CargoMessageController } from "../hooks/useSendCargoMessage";
import MessageFileUpload from "./MessageFileUpload";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.messages`;

const MessageComposer: React.FC<{ controller: CargoMessageController }> = ({ controller }) => {
  const { t } = useTranslation();
  const [dropError, setDropError] = useState<string | null>(null);
  const { text, staged, busy, canSend, inlineError } = controller;

  const onKeyDown: React.KeyboardEventHandler = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void controller.send();
    }
  };

  return (
    <div className="flex w-full shrink-0 flex-col" data-testid="drybulk-message-composer">
      <div className="flex flex-col gap-2 px-4 pt-2">
        {staged.map((file, index) => (
          <SingleFile
            key={file.documentUrl}
            testId={`drybulk-message-staged-${index}`}
            file={{ name: file.originalFileName ?? "" }}
            fieldName="drybulk-message-staged"
            setFiles={() => undefined}
            removeFn={() => controller.removeStaged(file.documentUrl)}
            disabled={busy}
          />
        ))}
        <MessageFileUpload
          testId="drybulk-message-upload"
          staged={staged.map((file) => ({
            name: file.originalFileName ?? "",
            url: file.documentUrl,
          }))}
          disabled={busy}
          onUpload={(files) => void controller.dropFiles(files)}
          onValidationResult={setDropError}
          errorMessage={dropError ?? undefined}
        />
      </div>
      <MessageBoxContainer>
        <MessageInput
          fullWidth
          multiline
          minRows={1}
          maxRows={8}
          disabled={busy}
          placeholder={t(`${L}.placeholder`)}
          value={text}
          onChange={(event) => controller.setText(event.target.value)}
          onKeyDown={onKeyDown}
          slotProps={{
            htmlInput: {
              "data-testid": "drybulk-message-input",
              "aria-label": t(`${L}.placeholder`),
            },
          }}
        />
      </MessageBoxContainer>
      <div className="flex flex-col px-4 pb-2">
        <FormHelperText data-testid="drybulk-message-character-rule">
          {t(`${L}.character-rule`)}
        </FormHelperText>
        {inlineError && (
          <FormHelperText error role="alert" data-testid="drybulk-message-error">
            {inlineError}
          </FormHelperText>
        )}
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button
          variant="contained"
          startIcon={<ContentPasteGo />}
          disabled={!canSend}
          onClick={() => void controller.send()}
          testId="drybulk-message-send"
          label={<span className="normal-case font-medium">{t(`${L}.send`)}</span>}
        />
      </div>
    </div>
  );
};

export default MessageComposer;
